import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail as MailIcon, MapPin, Clock, LogIn, ChevronDown } from "lucide-react";
import BrandLogo from "./BrandLogo";
import PageShell from "./PageShell";
import { SERVICE_CONTENT, SERVICE_ORDER } from "../pages/serviceContent";

/**
 * MarketingShell — shared header + footer for public marketing pages.
 * iter-140 adds a Services dropdown (desktop) and expanded submenu
 * (mobile) mirroring the source site's nav.
 */
const NAV_LINKS = [
    { to: "/", label: "Home", end: true },
    { to: "/about", label: "About" },
    // Services is rendered inline as a dropdown, not a plain link.
    { to: "/portfolio", label: "Portfolio" },
    { to: "/resources", label: "Resources" },
    { to: "/contact", label: "Contact" },
];

const SERVICE_LINKS = SERVICE_ORDER.map((slug) => ({
    to: `/services/${slug}`,
    label: SERVICE_CONTENT[slug].title,
}));

export default function MarketingShell({ children, testid = "marketing-page", contactStripe = true }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const location = useLocation();
    React.useEffect(() => setMobileOpen(false), [location.pathname]);

    const inServices = location.pathname.startsWith("/services");

    return (
        <PageShell testid={testid} className="bg-white">
            {contactStripe && (
            <div className="hero-dark contact-stripe hidden sm:flex items-center justify-around gap-4 bg-slate-900 text-slate-200 text-xs px-4 py-2">
                    <a href="tel:7053334376" className="inline-flex items-center gap-2 hover:text-white">
                        <Phone className="w-3.5 h-3.5 text-[#00A3FF]" /> 705-333-4376
                    </a>
                    <a href="mailto:info@steeltooth.ca" className="inline-flex items-center gap-2 hover:text-white">
                        <MailIcon className="w-3.5 h-3.5 text-[#00A3FF]" /> info@steeltooth.ca
                    </a>
                    <span className="inline-flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-[#00A3FF]" /> 3553 20 Sideroad, Innisfil ON
                    </span>
                </div>
            )}

            <header className="sticky top-0 z-40 flex items-center justify-between px-4 sm:px-8 py-3 backdrop-blur-md bg-white/90 border-b border-slate-200/60">
                <Link to="/" className="flex items-center" data-testid="marketing-logo">
                    <BrandLogo height={38} />
                </Link>

                <nav
                    className="hidden md:flex items-center gap-1 lg:gap-2"
                    data-testid="marketing-nav"
                    aria-label="Site sections"
                >
                    <NavLinkItem to="/" end>Home</NavLinkItem>
                    <NavLinkItem to="/about">About</NavLinkItem>

                    {/* Services dropdown (desktop, hover + focus). iter-166:
                        removed the mt-1 gap between button and menu; the
                        gap was killing hover intent — moving toward a
                        submenu row triggered onMouseLeave. Menu now sits
                        flush to the button and the wrapper's mouseleave
                        only fires when the pointer truly leaves both. */}
                    <div
                        className="relative"
                        onMouseEnter={() => setServicesOpen(true)}
                        onMouseLeave={() => setServicesOpen(false)}
                    >
                        <button
                            type="button"
                            onClick={() => setServicesOpen((v) => !v)}
                            aria-expanded={servicesOpen}
                            aria-haspopup="menu"
                            data-testid="marketing-services-toggle"
                            className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-sm transition-colors ${
                                inServices ? "text-[#007AFF]" : "text-slate-700 hover:text-[#007AFF]"
                            }`}
                        >
                            Services <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        {servicesOpen && (
                            <div
                                role="menu"
                                data-testid="marketing-services-menu"
                                className="absolute top-full left-0 pt-2 min-w-[220px]"
                            >
                                <div className="bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
                                    {SERVICE_LINKS.map((s) => (
                                        <NavLink
                                            key={s.to}
                                            to={s.to}
                                            className={({ isActive }) =>
                                                `block px-4 py-2.5 text-xs font-bold uppercase tracking-wider ${
                                                    isActive
                                                        ? "bg-[#007AFF]/10 text-[#007AFF]"
                                                        : "text-slate-800 hover:bg-slate-100 hover:text-[#007AFF]"
                                                }`
                                            }
                                            data-testid={`marketing-service-${s.to.split("/").pop()}`}
                                        >
                                            {s.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <NavLinkItem to="/portfolio">Portfolio</NavLinkItem>
                    <NavLinkItem to="/resources">Resources</NavLinkItem>
                    <NavLinkItem to="/contact">Contact</NavLinkItem>

                    <Link
                        to="/login"
                        className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#007AFF] text-white text-sm font-bold hover:bg-[#0369A1]"
                        data-testid="marketing-signin"
                    >
                        <LogIn className="w-4 h-4" /> Sign in
                    </Link>
                </nav>

                <button
                    className="md:hidden w-10 h-10 flex items-center justify-center rounded-md border border-slate-200 text-slate-800"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="marketing-hamburger"
                >
                    {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </header>

            {mobileOpen && (
                <div className="md:hidden border-b border-slate-200 bg-white" data-testid="marketing-mobile-menu">
                    <div className="flex flex-col px-4 py-2">
                        <MobileLink to="/" end>Home</MobileLink>
                        <MobileLink to="/about">About</MobileLink>
                        <div className="border-b border-slate-100 py-1">
                            <div className="py-3 text-sm font-bold uppercase tracking-wide text-slate-800 flex items-center justify-between">
                                Services
                            </div>
                            {SERVICE_LINKS.map((s) => (
                                <MobileLink key={s.to} to={s.to} nested>{s.label}</MobileLink>
                            ))}
                        </div>
                        <MobileLink to="/portfolio">Portfolio</MobileLink>
                        <MobileLink to="/resources">Resources</MobileLink>
                        <MobileLink to="/contact">Contact</MobileLink>
                        <Link
                            to="/login"
                            className="mt-2 mb-2 py-2 text-center rounded-md bg-[#007AFF] text-white text-sm font-bold"
                            data-testid="marketing-mobile-signin"
                        >
                            Sign in
                        </Link>
                    </div>
                </div>
            )}

            {children}

            <footer className="hero-dark bg-slate-900 text-slate-300 mt-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 text-sm">
                    <div>
                        <BrandLogo height={40} />
                        <p className="mt-3 text-slate-400 leading-relaxed">
                            Excavation, septic, demolition and site services across Simcoe County.
                        </p>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-white font-bold mb-3">Contact</div>
                        <ul className="space-y-2">
                            <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-[#00A3FF]" /><a href="tel:7053334376" className="hover:text-white">705-333-4376</a></li>
                            <li className="flex items-start gap-2"><MailIcon className="w-4 h-4 mt-0.5 text-[#00A3FF]" /><a href="mailto:info@steeltooth.ca" className="hover:text-white break-all">info@steeltooth.ca</a></li>
                            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#00A3FF]" /><span>3553 20 Sideroad, Innisfil ON</span></li>
                            <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-[#00A3FF]" /><span>7 AM – 5 PM · Mon–Fri</span></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-white font-bold mb-3">Services</div>
                        <ul className="space-y-2">
                            {SERVICE_LINKS.map((s) => (
                                <li key={s.to}>
                                    <Link to={s.to} className="hover:text-white">{s.label}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-white font-bold mb-3">Company</div>
                        <ul className="space-y-2">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></li>
                            ))}
                            <li><Link to="/privacy" className="hover:text-white">Privacy policy</Link></li>
                            <li><Link to="/terms" className="hover:text-white">Terms of service</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
                    © {new Date().getFullYear()} Steeltooth Contracting · Takeoff · Estimate · Deliver
                </div>
            </footer>
        </PageShell>
    );
}

function NavLinkItem({ to, end, children }) {
    return (
        <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
                `px-3 py-2 text-sm font-bold uppercase tracking-wide rounded-sm transition-colors ${
                    isActive ? "text-[#007AFF]" : "text-slate-700 hover:text-[#007AFF]"
                }`
            }
            data-testid={`marketing-nav-${String(children).toLowerCase()}`}
        >
            {children}
        </NavLink>
    );
}

function MobileLink({ to, end, nested, children }) {
    return (
        <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
                `py-3 text-sm font-bold uppercase tracking-wide border-b border-slate-100 last:border-0 ${
                    nested ? "pl-4" : ""
                } ${isActive ? "text-[#007AFF]" : "text-slate-800"}`
            }
        >
            {children}
        </NavLink>
    );
}
